import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { api } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import { formatCop as formatCopRaw } from 'common/src/other/currencyFormat';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2AddButton,
  C2EmptyState,
  C2DocsChip,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
  C2ProgressBar,
  C2Switch,
  c2PillTabsSx,
} from '../components/Table/C2UserTable';
import WalletCampaignsPanel from '../components/promos/WalletCampaignsPanel';

const STATUS_UI = {
  active: { tone: 'success', label: 'Activo' },
  hidden: { tone: 'neutral', label: 'Oculto' },
  expired: { tone: 'danger', label: 'Vencido' },
  exhausted: { tone: 'warning', label: 'Agotado' },
};

const formatCop = (v) => formatCopRaw(Math.round(Number(v) || 0));

const discountLabel = (p) =>
  p.promo_discount_type === 'percentage' ? `${Number(p.promo_discount_value) || 0}%` : formatCop(p.promo_discount_value);

export default function Promos() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const settings = useSelector((s) => s.settingsdata.settings);
  const promodata = useSelector((s) => s.promodata);
  const canEdit = !!settings.AllowCriticalEditsAdmin;

  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(state?.pageNo || 0);
  const [statusFilter, setStatusFilter] = useState('');
  const [tab, setTab] = useState(state?.tab === 'campaigns' ? 'campaigns' : 'codes');

  useEffect(() => {
    setCurrentPage(state?.pageNo || 0);
  }, [state]);

  const rows = useMemo(() => {
    const now = Date.now();
    return [...(promodata.promos || [])]
      .map((p) => ({ ...p, status: api.promoStatus(p, now) }))
      .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
  }, [promodata.promos]);

  const summary = useMemo(
    () => ({
      active: rows.filter((r) => r.status === 'active').length,
      expired: rows.filter((r) => r.status === 'expired').length,
      exhausted: rows.filter((r) => r.status === 'exhausted').length,
      uses: rows.reduce((acc, r) => acc + (Number(r.user_avail) || 0), 0),
    }),
    [rows]
  );

  const filtered = statusFilter ? rows.filter((r) => r.status === statusFilter) : rows;

  const translated = (text) => (text ? t(getLangKey(text)) : '');

  const columns = [
    {
      title: 'Promoción',
      field: 'promo_name',
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 180 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>{translated(r.promo_name) || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>{translated(r.promo_description)}</Typography>
        </Box>
      ),
    },
    {
      title: 'Código',
      field: 'promo_code',
      render: (r) => <C2DocsChip icon={ConfirmationNumberOutlinedIcon} label={r.promo_code || '—'} />,
    },
    {
      title: 'Descuento',
      field: 'promo_discount_value',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.95rem', color: C2.violet }}>{discountLabel(r)}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>
            {r.promo_discount_type === 'percentage' && Number(r.max_promo_discount_value) > 0 ? `Tope ${formatCop(r.max_promo_discount_value)} · ` : ''}
            {Number(r.min_order) > 0 ? `Mínimo ${formatCop(r.min_order)}` : 'Sin mínimo'}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Vigencia',
      field: 'promo_validity',
      render: (r) => {
        if (!r.promo_validity) return <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.muted }}>Sin vencimiento</Typography>;
        const expired = r.status === 'expired';
        return (
          <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.84rem', color: expired ? '#B91C1C' : C2.sidebar }}>
              {moment(r.promo_validity).format('lll')}
            </Typography>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>
              {expired ? `Venció ${moment(r.promo_validity).fromNow()}` : `Vence ${moment(r.promo_validity).fromNow()}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      title: 'Usos',
      field: 'user_avail',
      render: (r) =>
        Number(r.promo_usage_limit) > 0 ? (
          <C2ProgressBar used={Number(r.user_avail) || 0} total={Number(r.promo_usage_limit)} done={r.status === 'exhausted'} />
        ) : (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.dark, whiteSpace: 'nowrap' }}>
            <b>{Number(r.user_avail) || 0}</b> usos · sin límite
          </Typography>
        ),
    },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => <C2StatusChip tone={STATUS_UI[r.status].tone} label={STATUS_UI[r.status].label} />,
    },
    {
      title: 'Visible en la app',
      field: 'promo_show',
      render: (r) => (
        <C2Switch
          checked={!!r.promo_show}
          disabled={!canEdit}
          onClick={(e) => e.stopPropagation()}
          onChange={() => dispatch(api.setPromoVisibility(r.id, !r.promo_show))}
        />
      ),
    },
  ];

  const tableComponents = c2Toolbar(
    <C2Select
      key="status"
      id="promo-status-filter"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={160}
      options={[
        { value: '', label: 'Todos' },
        { value: 'active', label: 'Activos' },
        { value: 'hidden', label: 'Ocultos' },
        { value: 'expired', label: 'Vencidos' },
        { value: 'exhausted', label: 'Agotados' },
      ]}
    />,
    <C2AddButton key="add" label="Crear promoción" onClick={() => navigate('/promos/editpromo', { state: { pageNo: currentPage } })} />
  );

  if (promodata.loading) return <CircularLoading />;

  return (
    <ThemeProvider theme={c2TableTheme}>
      <Box sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={c2PillTabsSx}>
          <Tab value="codes" label="Códigos de descuento" />
          <Tab value="campaigns" label="Campañas de saldo" />
        </Tabs>
      </Box>

      {tab === 'campaigns' ? (
        <WalletCampaignsPanel />
      ) : (
      <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={CheckCircleOutlineRoundedIcon} label="Promos activos" value={summary.active} caption="Visibles y vigentes" accent="#16A34A" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={EventBusyOutlinedIcon} label="Vencidos" value={summary.expired} caption="Pasó su fecha de fin" accent={summary.expired ? '#DC2626' : C2.violet} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={LocalOfferOutlinedIcon} label="Agotados" value={summary.exhausted} caption="Llegaron al límite de usos" accent={summary.exhausted ? '#D97706' : C2.violet} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={ConfirmationNumberOutlinedIcon} label="Usos totales" value={summary.uses} caption="Suma de todos los promos" />
        </Grid>
      </Grid>

      <C2TableCard>
        <C2SectionHeading
          title="Promociones"
          subtitle="Códigos de descuento que los usuarios aplican al pagar su viaje. Solo se muestran en la app los activos marcados como visibles."
        />
        <MaterialTable
          title={t('promo_offer_title')}
          columns={columns}
          data={filtered}
          style={c2TableStyle(isRTL)}
          components={tableComponents}
          onChangePage={(page) => setCurrentPage(page)}
          onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
          options={{ ...c2TableOptions(selectedRow), initialPage: state?.pageNo || 0, actionsColumnIndex: -1 }}
          localization={c2Localization(
            localization(t),
            <C2EmptyState
              icon={LocalOfferOutlinedIcon}
              title={statusFilter ? 'No hay promociones con este estado' : 'Aún no hay promociones'}
              message="Crea un código de descuento para atraer y fidelizar a tus usuarios."
            />
          )}
          editable={{
            onRowDelete: (oldData) =>
              canEdit ? dispatch(api.editPromo(oldData, 'Delete')) : Promise.resolve(alert(t('demo_mode'))),
          }}
          actions={[
            {
              icon: () => <C2DocsChip icon={EditOutlinedIcon} label="Editar" />,
              tooltip: t('edit'),
              onClick: (event, row) => navigate(`/promos/editpromo/${row.id}`, { state: { pageNo: currentPage } }),
            },
          ]}
        />
      </C2TableCard>
      </>
      )}
    </ThemeProvider>
  );
}
